/* eslint-disable max-len */
/**
 * TdSelect — styled select that matches TdInput's visual treatment.
 *
 * Why not a plain <select>? Native selects inside fixed/transformed
 * containers (e.g. Headless UI Dialog) frequently mis-position their
 * dropdown — the menu can fly to the top-left of the modal instead of
 * anchoring under the trigger. This component uses Headless UI Listbox,
 * which renders the menu through React and keeps it anchored.
 *
 * The component is uncontrolled-via-react-hook-form: the parent passes
 * `value` and `onChange`. Use a Controller in the form to wire it.
 */
import React from 'react';
import {
  Listbox, ListboxButton, ListboxOption, ListboxOptions,
} from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import ValidationIndicator from '../tdinput/ValidationIndicator';

export type TdSelectOption = { value: string; label: string };

type Props = {
  id: string;
  name: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: TdSelectOption[];
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  isValid?: boolean;
  width?: string;
  /** Reserve the error line's height when there is no error (no layout shift on blur validation). */
  reserveErrorSpace?: boolean;
};

export const TdSelect: React.FC<Props> = ({
  id, name, label, value, onChange, options, placeholder = 'Select…',
  required = false, error = false, errorMessage, isValid = false, width, reserveErrorSpace = false,
}) => {
  const showInvalid = !!error;
  const showValid = !showInvalid && isValid;
  const stateClass = showInvalid
    ? 'border-2 border-doqit-highlight-pink text-doqit-highlight-pink'
    : 'border border-doqit-purple-150 text-doqit-deep-purple';
  const widthClass = width || 'w-full';
  const baseInputClass = 'h-[71px] rounded-[12px] font-body font-medium text-[19px] leading-[19px] tracking-[0.5px] bg-doqit-purple-25 outline-none';
  const insetShadow = 'shadow-[inset_2px_2px_4px_rgba(57,38,79,0.2)]';
  const triggerClass = `flex items-center justify-between ${widthClass} pl-[21px] pr-[16px] ${baseInputClass} ${insetShadow} ${stateClass} text-left`;

  const selected = options.find((o) => o.value === value);

  return (
    <label htmlFor={id} className="relative block">
      {label && (
        <span
          className="block text-doqit-mid-purple text-[15px] leading-[19px] tracking-[0.5px] font-medium font-body mb-1.5 min-h-[38px]"
        >
          {label}
          {required && <span className="text-doqit-highlight-pink ml-0.5">*</span>}
        </span>
      )}
      <Listbox value={value} onChange={onChange} name={name}>
        <div className="relative">
          <ListboxButton id={id} className={triggerClass}>
            <span className={selected ? 'truncate' : 'truncate opacity-70'}>
              {selected ? selected.label : placeholder}
            </span>
            <ChevronDownIcon className="w-5 h-5 ml-2 text-doqit-mid-purple/70 flex-shrink-0" />
          </ListboxButton>
          <ListboxOptions
            anchor="bottom start"
            className="w-[var(--button-width)] mt-1 rounded-[12px] bg-white shadow-lg border border-doqit-purple-150 overflow-hidden z-[60] focus:outline-none max-h-60 overflow-y-auto"
          >
            {options.map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                className="group flex items-center gap-2 cursor-pointer px-4 py-2.5 text-[15px] text-doqit-deep-purple data-[focus]:bg-doqit-purple-25"
              >
                <CheckIcon className="invisible group-data-[selected]:visible w-4 h-4 text-doqit-mid-purple" />
                <span className="truncate">{option.label}</span>
              </ListboxOption>
            ))}
          </ListboxOptions>
          {showInvalid && (
            <ValidationIndicator state="invalid" className="absolute right-10 -top-[10px]" />
          )}
          {showValid && (
            <ValidationIndicator state="valid" className="absolute right-10 -top-[10px]" />
          )}
        </div>
      </Listbox>
      {showInvalid && errorMessage && (
        <p className={`${name}-error mt-1.5 text-doqit-highlight-pink text-[15px] leading-[19px] tracking-[0.5px] font-bold font-body`}>
          {errorMessage}
        </p>
      )}
      {/* Holds the error line's height while there is no error, so blur
          validation doesn't push everything below it down (opt-in). */}
      {reserveErrorSpace && !(showInvalid && errorMessage) && (
        <span aria-hidden="true" className="block mt-1.5 h-[19px]" />
      )}
    </label>
  );
};

export default TdSelect;
